import { Group, Mesh, ShaderMaterial, DoubleSide } from 'three';

import ModelGLB from '../_app/cuchillo/3D/ModelGLB';
import TexturesController from '../_app/cuchillo/3D/TexturesController';
import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import { ControllerPage } from '../_app/cuchillo/pages/ControllerPage';
import { PLANE_GEOMETRY } from './constants';
import { THUMBNAIL_OBJECT_VERTEX, THUMBNAIL_OBJECT_FRAGMENT } from './shaders/thumbnail-object';

// Posició del pla d'imatge en l'eix de profunditat.
const PLANE_Z = 0;

// Thumbnail com a pla amb la imatge del producte. Abans portava també un marc en 3D
// (tag.glb) amb MeshPhysicalMaterial(transmission:1) per l'efecte de vidre — molt car de
// renderitzar (Three.js necessita un pass de renderitzat addicional per la transmissió,
// per CADA instància, CADA frame). Es treu per rendiment; queda només el pla, que ja
// existia i ja era barat (ShaderMaterial propi, sense transmission).
// Manté la mateixa API que ProductsScene/ProductScene esperen: webglGroup, rotationGroup,
// mesh, pos, update/resize/dispose i el control d'opacitat via `_op` (proxy que gsap pot tweenar).
export default class ThumbnailObject extends ModelGLB {
    _op = { value: 1 };

    constructor(opts = {}) {
        super(opts);

        this.link = opts.link || '';

        // webglGroup entre `this` i rotationGroup (counter-rotació + escala de profunditat
        // les aplica ProductsScene). rotationGroup (de WebGLObject) rep el tilt del ratolí.
        this.webglGroup = new Group();
        this.webglGroup.add(this.rotationGroup);
        this.add(this.webglGroup);
    }

    init() {
        return new Promise((resolve) => {
            // Pla amb la imatge del producte (encarat a càmera, +Z). ShaderMaterial propi
            // perquè ProductsScene hi pugui aplicar el blur (bokeh).
            this._planeMaterial = new ShaderMaterial({
                uniforms: {
                    map: { value: null },
                    opacity: { value: this._op.value },
                    blur: { value: 0 },
                    cover: { value: { x: 1, y: 1 } },
                },
                vertexShader: THUMBNAIL_OBJECT_VERTEX,
                fragmentShader: THUMBNAIL_OBJECT_FRAGMENT,
                transparent: true,
                side: DoubleSide,
                depthWrite: false,
            });
            this.mesh = new Mesh(PLANE_GEOMETRY, this._planeMaterial);
            // El camp d'instància `material` (de WebGLObject) eclipsa el getter de
            // ModelGLB, així que l'assignem explícitament → ProductsScene llegeix
            // item.material.uniforms.blur sobre el ShaderMaterial del pla.
            this.material = this._planeMaterial;
            this.rotationGroup.add(this.mesh);

            // Clic → navega al producte (raycaster sobre la imatge). Només si hi ha
            // link (el header de Product no en té → no ha de ser clicable).
            if (this.link) {
                this.mesh.userData.isClickable = true;
                this.mesh.userData.link = this.link;
                this.mesh.userData.onClick = () => ControllerPage.changePage(this.link);
                WebGLSketch.addInteractive(this.mesh);
            }

            // Textura (PNG del thumbnail, des de data-src)
            const src = this.dom.dataset.src;
            if (src) {
                TexturesController.load({
                    src,
                    call: (texture) => {
                        texture.needsUpdate = true;
                        this._planeMaterial.uniforms.map.value = texture;
                        this._planeMaterial.needsUpdate = true;
                    },
                });
            }

            // Dimensions natives de la imatge per al càlcul de cover
            this._imgW = parseFloat(this.dom.dataset.width) || 1;
            this._imgH = parseFloat(this.dom.dataset.height) || 1;

            this.resize();
            resolve();
        });
    }

    setOpacity(value) {
        this._op.value = value;
        this._applyOpacity(value);
    }

    _applyOpacity(value) {
        if (this._planeMaterial) this._planeMaterial.uniforms.opacity.value = value;
    }

    update() {
        super.update();
        this._applyOpacity(this._op.value);
    }

    resize() {
        if (!this.mesh) return;

        const { width, height } = this.dom.getBoundingClientRect();
        if (width <= 0 || height <= 0) return;

        // Pla d'imatge a mida real (1:1 amb el slot del DOM)
        this.mesh.scale.set(width, height, 1);
        this.mesh.position.z = PLANE_Z;

        // Cover: la textura omple el pla conservant aspect (retalla el sobrant)
        const planeA = width / height;
        const imgA = (this._imgW || 1) / (this._imgH || 1);
        const cover = this._planeMaterial.uniforms.cover.value;
        if (imgA > planeA) {
            cover.x = planeA / imgA;
            cover.y = 1;
        } else {
            cover.x = 1;
            cover.y = imgA / planeA;
        }
    }

    dispose() {
        if (this.mesh) {
            WebGLSketch.removeInteractive(this.mesh);
            this.rotationGroup.remove(this.mesh);
        }

        // Només material propi (PLANE_GEOMETRY és compartida entre instàncies → no es disposa).
        if (this._planeMaterial) this._planeMaterial.dispose();

        this.webglGroup.remove(this.rotationGroup);
        this.remove(this.webglGroup);
    }
}
